/**
 * UI ICON RESOLVER — one place that turns an icon id into a URL.
 *
 * ── WHY ────────────────────────────────────────────────────────────────────
 * 48 painted, game-style icons are ingested into `frontend/public/ui/` by
 * `tools/assetgen/scripts/ingest-ui-icons.mjs`, alongside the three currency
 * marks that were hand-trimmed there earlier (`gold`, `shards`, `sigils` —
 * `meta/meta-ui.ts` names those three itself and predates this module). Before
 * this file the only way for a screen to show one was to type a path, which is
 * how an asset folder ends up "complete and imported by nothing": nothing
 * fails when the name is wrong, the picture is simply absent.
 *
 * So ids are checked against `UI_ICONS`, the list of files this repo actually
 * ships. An unknown id resolves to `''` and `createIcon` returns null — the
 * caller draws what it drew before the icon existed. `test/ui-icons.test.ts`
 * asserts the list matches the folder, so a deleted file is a red test rather
 * than a hole on a screen.
 *
 * ── THE SPA-FALLBACK TRAP ──────────────────────────────────────────────────
 * `engine/ui/card-art.ts` documents it for the manifest: a dev server answers
 * a missing asset with `index.html` and a 200, so a 200 proves nothing. An
 * `<img>` cannot check a body, but it does not have to — an HTML payload fails
 * to DECODE, and the element fires `error`. That is the hook used here: on
 * `error` the image is hidden (`hidden`, so it is out of the a11y tree too)
 * rather than left as a browser's broken-image glyph, which is the ONE outcome
 * that must never reach a player. Whatever the icon decorated — a label, a
 * text glyph, a `createPlaceholder` box — is still there, because every icon
 * from this module is decorative and sits BESIDE its meaning, never instead of
 * it.
 *
 * ── EVERY ICON IS DECORATIVE ───────────────────────────────────────────────
 * `alt=""` plus `aria-hidden`, always. An icon-only control is unusable for
 * anyone who has not learned the icon set, so the accessible name stays on the
 * label or the control. This mirrors what `currencyChip` already does for the
 * three currency marks.
 */

/** Where the ingested icons are served from, relative to the site root. */
export const UI_ICON_BASE = '/ui/';

/**
 * Every icon id this repo ships, sorted.
 *
 * Kept as a literal rather than read from the filesystem — the frontend has no
 * filesystem — and pinned to the folder by `test/ui-icons.test.ts`.
 */
export const UI_ICONS = Object.freeze([
  'card-back',
  'card-copies-stack',
  'card-level-up',
  'cost-pip-mana',
  'crate-cracked-seam',
  'crate-keepstone-vault',
  'crate-riftgate-hoard',
  'crate-seal-broken',
  'crate-slot-empty',
  'crate-slot-ready',
  'crate-slot-unlocking',
  'crate-warded-cache',
  'currency-gold-coins',
  'currency-gold-pile',
  'currency-shard-cluster',
  'currency-shard-single',
  'deck-slot-empty',
  'dust-motes-bowl',
  'gold',
  'house-banner-blank',
  'house-donate',
  'house-war-standard',
  'mode-draft',
  'mode-tournament',
  'mode-training',
  'nav-battle',
  'nav-cards',
  'nav-events',
  'nav-house',
  'nav-leaderboard',
  'nav-profile',
  'nav-settings',
  'nav-shop',
  'price-plate-blank',
  'quest-complete-tick',
  'quest-daily',
  'quest-reroll-swap',
  'quest-weekly',
  'rarity-common-cut',
  'rarity-epic-notched-gem',
  'rarity-mythic-split-frame',
  'rarity-rare-double-cut',
  'reward-bundle',
  'role-leader',
  'role-officer',
  'shards',
  'sigil-rank-seal',
  'sigils',
  'social-blocked',
  'social-friends',
  'social-spectate',
  'tile-deploy-vfx',
  'tile-emote-wheel',
  'tile-tower-skin',
  'timer-hourglass',
  'track-premium-mark',
  'warpath-node-claimed',
  'warpath-node-locked',
  'xp-mote',
] as const);

/** An id known to be shipped. */
export type UiIconId = (typeof UI_ICONS)[number];

const KNOWN: ReadonlySet<string> = new Set<string>(UI_ICONS);

/** True when `id` names an icon this build ships. */
export function hasIcon(id: string): id is UiIconId {
  return KNOWN.has(id);
}

/**
 * URL for an icon id, or `''` when nothing ships under that name.
 *
 * `''` and not a fallback picture on purpose: a wrong-but-present icon is
 * worse than none, because it reads as a deliberate meaning.
 */
export function iconUrl(id: string): string {
  return KNOWN.has(id) ? `${UI_ICON_BASE}${id}.png` : '';
}

export interface IconOptions {
  /** Rendered size in CSS px, square. Defaults to 20. */
  readonly size?: number;
  /** Extra classes, merged after `rga-icon`. */
  readonly class?: string;
}

/**
 * A decorative `<img>` for an icon id, or null when the id is unknown.
 *
 * Null is a real branch the callers handle: they append it only if it exists,
 * so a screen built against an icon that was never ingested renders exactly as
 * it did before, with no gap and no broken glyph.
 */
export function createIcon(
  doc: Document,
  id: string,
  options: IconOptions = {},
): HTMLImageElement | null {
  const url = iconUrl(id);
  if (url === '') return null;
  const size = options.size ?? 20;
  const img = doc.createElement('img');
  img.className = options.class === undefined ? 'rga-icon' : `rga-icon ${options.class}`;
  img.src = url;
  // Decorative, twice over: an empty alt keeps it out of the accessible name,
  // aria-hidden keeps it out of the tree even where an alt is ignored.
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  img.width = size;
  img.height = size;
  img.decoding = 'async';
  // Sizing and alignment inline rather than through a shared sheet: this
  // module owns no CSS and injecting one more <style> for two declarations is
  // more machinery than the two declarations. `contain` matters — the ingested
  // files are square, but an icon that is ever re-cut must letterbox, not crop.
  img.style.width = `${size}px`;
  img.style.height = `${size}px`;
  img.style.objectFit = 'contain';
  img.style.verticalAlign = 'middle';
  img.loading = 'lazy';
  // A 200 with an HTML body decodes as nothing; so does a real 404. Either way
  // the element goes away instead of showing a broken-image glyph.
  img.addEventListener('error', () => {
    img.hidden = true;
  });
  return img;
}
